// ═══════════════════════════════════════════════════════════════════════════
// AI Command Schemas — BASE-PRIMITIVE + BASE-MODIFY family (CC-2c sub-arm 4)
// ═══════════════════════════════════════════════════════════════════════════
//
// Zod schemas describing the JSON the AI produces when it wants to CREATE one
// of the base drawing primitives, or apply one of the base MODIFY transforms,
// through the safe contour. As with every other AI-command domain, each schema
// here is a PLAIN DATA CONTRACT — it does NOT mutate anything. Translation to
// internal `EditorCommand`s happens in `basePrimDispatcher.ts`, and the reducer
// pipeline (the same path the UI / command line dispatch through) is the only
// place that ever writes to the document. This enforces the CLAUDE.md rules
// that AI never mutates the document directly and that geometry only ever
// changes through the Command Engine.
//
// This is the FINAL CC-2c sub-arm — it completes the AI dispatcher matrix.
//
// SCOPE — the base primitives + base modify commands that are BOTH
// `aiCallable: true` in the registry AND listed AI-callable in ТЗ §10.1, MINUS
// the arms owned by other CC-2c sub-arms:
//   • Base primitives (create) — this sub-arm:
//       CREATE_LINE   → LINE   → geometry/create-line
//       CREATE_CIRCLE → CIRCLE → geometry/create-circle
//       CREATE_ARC    → ARC    → geometry/create-arc
//       CREATE_PLINE  → PLINE  → geometry/create-lwpolyline
//   • Base modify — this sub-arm:
//       MOVE   → MOVE   → geometry/move-entities   (delta)
//       COPY   → COPY   → geometry/copy            (base + second point, 1:N
//                                                   for multi-drop — the same
//                                                   mutating command the manual
//                                                   COPY emits)
//       ROTATE → ROTATE → geometry/rotate-entities (basePoint + angleDeg)
//       DELETE_ENTITIES → ERASE → shell/delete-entities (entityIds; the SAME
//                                                   command the Delete key emits —
//                                                   T3 agent-editing erase arm)
//   • Excluded — registry aiCallable:FALSE (would fail the CC-2a guard; absent
//     from ТЗ §10.1, AI surface deferred per the descriptor comment + ТЗ §17):
//       POINT   (command-registry/src/commands/drawing.ts:119 — aiCallable:false)
//       ELLIPSE (command-registry/src/commands/drawing.ts:494 — aiCallable:false)
//   • Excluded — owned by sibling sub-arms (do NOT duplicate):
//       MIRROR / SCALE / STRETCH / OFFSET / EXTEND / TRIM / ARRAYRECT /
//         ARRAYEDIT → draw-modify (CC-2c sub-arm 2)
//       TEXT / MTEXT / HATCH / DIMENSION / MLEADER / REVCLOUD → entity-creation
//         (CC-2c sub-arm 1)
//
// Design notes (mirror entitySchemas.ts / drawModifySchemas.ts):
//   - Every action carries a literal `action: "..."` discriminator so the
//     top-level schemas are Zod discriminated unions, and so the action sets
//     stay DISJOINT from the block / entity / draw-modify domains (enables the
//     `aiDispatcher.ts` router to pick the owning domain by `action`).
//   - Point objects reuse the shared `aiPoint2DSchema` / `aiPoint3DSchema` from
//     blockSchemas — one point-contract truth across the package. LINE / CIRCLE
//     / ARC points are 3D ({ x, y, z }, z defaults to 0) because the
//     `geometry/create-{line,circle,arc}` contracts carry a z (group 30); the
//     AI may author in the z = 0 plane (default) or supply a real z. PLINE
//     vertices are the 2D LWPOLYLINE vertex shape; MOVE/COPY delta + ROTATE
//     basePoint are 2D ({ x, y }), matching their contracts.
//   - `spaceId` is optional on the create commands; the dispatcher resolves it
//     to the active space via `pickSpaceId` (matching INSERT_BLOCK / the entity
//     domain). The base-modify commands are self-contained (entity ids).
//
// ANGLE UNITS (verified against the reducer — no double-conversion):
//   - ARC `startAngle` / `endAngle` are in DEGREES, passed THROUGH verbatim:
//     the create-arc reducer stores them unchanged on the Arc entity, and the
//     draft state machine builds them in degrees (`toStageAngle` returns
//     degrees), matching DXF group codes 50/51.
//   - ROTATE `angleDeg` is in DEGREES, passed THROUGH verbatim: the
//     `geometry/rotate-entities` reducer feeds it to `rotatePoint(p, base,
//     angleDeg)`, which converts degrees → radians internally
//     (`geometryTransforms.ts`: `rad = angleDeg * Math.PI / 180`). A
//     degrees→radians conversion here would be a double-conversion bug.

import { z } from "zod";

import { aiPoint2DSchema, aiPoint3DSchema } from "./blockSchemas";

// ═══════════════════════════════════════════════════════════════════════════
// BASE PRIMITIVES (create)
// ═══════════════════════════════════════════════════════════════════════════

// ─── CREATE_LINE → geometry/create-line ─────────────────────────────────────
//
// A single LINE segment from `start` to `end`. Points are 3D ({ x, y, z }, z
// defaults to 0) — the `geometry/create-line` contract carries a z (group 30).
export const createLineAiSchema = z.object({
  action: z.literal("CREATE_LINE"),
  spaceId: z.string().min(1).optional(),
  start: aiPoint3DSchema,
  end: aiPoint3DSchema,
});
export type CreateLineAiCommand = z.infer<typeof createLineAiSchema>;

// ─── CREATE_CIRCLE → geometry/create-circle ─────────────────────────────────
//
// `radius` must be positive (the contract enforces the same). `center` is 3D
// ({ x, y, z }, z defaults to 0) per the contract.
export const createCircleAiSchema = z.object({
  action: z.literal("CREATE_CIRCLE"),
  spaceId: z.string().min(1).optional(),
  center: aiPoint3DSchema,
  radius: z.number().positive(),
});
export type CreateCircleAiCommand = z.infer<typeof createCircleAiSchema>;

// ─── CREATE_ARC → geometry/create-arc ───────────────────────────────────────
//
// `startAngle` / `endAngle` are in DEGREES (CCW from +X, DXF group codes
// 50/51) and stored verbatim by the reducer. The contract's center+radius+
// start/end-angle form is the canonical one (there is no separate 3-point
// contract command — the 3-point draft flow resolves to center+radius+angles
// before dispatch).
export const createArcAiSchema = z.object({
  action: z.literal("CREATE_ARC"),
  spaceId: z.string().min(1).optional(),
  center: aiPoint3DSchema,
  radius: z.number().positive(),
  // DEGREES — stored verbatim on the Arc entity (group codes 50/51).
  startAngle: z.number(),
  endAngle: z.number(),
});
export type CreateArcAiCommand = z.infer<typeof createArcAiSchema>;

// ─── CREATE_PLINE → geometry/create-lwpolyline ──────────────────────────────
//
// A lightweight polyline (LWPOLYLINE). Each vertex carries the full LWPOLYLINE
// per-vertex fidelity — `bulge` (DXF code 42, arc segment), `startWidth` /
// `endWidth` (codes 40/41) — not a simplified point list. At least two
// vertices are required; `closed` adds the closing segment (default open,
// matching AutoCAD PLINE).
export const aiPlineVertexSchema = z.object({
  x: z.number(),
  y: z.number(),
  bulge: z.number().default(0),
  startWidth: z.number().default(0),
  endWidth: z.number().default(0),
});
export type AiPlineVertex = z.infer<typeof aiPlineVertexSchema>;

export const createPlineAiSchema = z.object({
  action: z.literal("CREATE_PLINE"),
  spaceId: z.string().min(1).optional(),
  vertices: z.array(aiPlineVertexSchema).min(2),
  closed: z.boolean().default(false),
});
export type CreatePlineAiCommand = z.infer<typeof createPlineAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// BASE MODIFY
// ═══════════════════════════════════════════════════════════════════════════

// ─── MOVE → geometry/move-entities ──────────────────────────────────────────
//
// AutoCAD MOVE: select objects, specify base point, specify second point of
// displacement. The displacement (the contract `delta`) equals
// (secondPoint − basePoint); the dispatcher computes it so the audit log / AI
// replay captures the exact two points the AI reasoned about (matching the
// draft steps select → base → destination), rather than a bare vector.
export const moveAiSchema = z.object({
  action: z.literal("MOVE"),
  entityIds: z.array(z.string().min(1)).min(1),
  basePoint: aiPoint2DSchema,
  secondPoint: aiPoint2DSchema,
});
export type MoveAiCommand = z.infer<typeof moveAiSchema>;

// ─── COPY → geometry/copy (1:N multi-drop) ──────────────────────────────────
//
// AutoCAD COPY drops one OR several copies in a single session: after the base
// point, each subsequent destination drops another copy (e.g.
// `CO (sel) 0,0 @50,0 @100,0` — two copies). `secondPoints` is therefore an
// array (≥1); the dispatcher emits ONE `geometry/copy` per destination carrying
// the explicit base + second point pair, so the geometry reducer clones the
// selection translated by (secondPoint − basePoint), the originals stay put and
// N offset clones are produced. This is full-fidelity multi-copy — not a single
// flattened delta — and routes through the SAME mutating command as the manual
// COPY (`geometry/copy`), not the audit-only `geometry/copy-entities`.
export const copyAiSchema = z.object({
  action: z.literal("COPY"),
  entityIds: z.array(z.string().min(1)).min(1),
  basePoint: aiPoint2DSchema,
  secondPoints: z.array(aiPoint2DSchema).min(1),
});
export type CopyAiCommand = z.infer<typeof copyAiSchema>;

// ─── ROTATE → geometry/rotate-entities ──────────────────────────────────────
//
// Rotate the selection about `basePoint` by `angleDeg` DEGREES (positive =
// CCW, per ANGDIR = 0). `angleDeg` is passed THROUGH verbatim — the reducer's
// `rotatePoint` converts degrees → radians internally (see the module header);
// converting here would double-convert. The angle must be finite (mirrors the
// `geometry/rotate-entities` contract refine).
export const rotateAiSchema = z.object({
  action: z.literal("ROTATE"),
  entityIds: z.array(z.string().min(1)).min(1),
  basePoint: aiPoint2DSchema,
  angleDeg: z
    .number()
    .refine((a) => !Number.isNaN(a) && Number.isFinite(a), {
      message: "Rotation angle must be finite",
    }),
});
export type RotateAiCommand = z.infer<typeof rotateAiSchema>;

// ─── DELETE_ENTITIES → shell/delete-entities (ERASE) ─────────────────────────
//
// AutoCAD ERASE: select objects, then erase them all at once. The agent supplies
// the `entityIds` (the SAME stable `DrawingEntity.id`s the T1 read-API projects
// and the T2 safe-contour returns as `createdEntityIds`) — the create→read→erase
// loop's erase. Translates 1:1 to the EXISTING `shell/delete-entities` command
// (the Delete-key path), already undoable via the inverse `shell/restore-entities`.
// No new write path, no new reducer — this only exposes the existing delete as an
// AI-callable surface. `entityIds` ≥1 mirrors the contract's `.min(1)`; unknown /
// stale / foreign ids are skipped gracefully by the reducer (no-op, no throw), so
// the agent passing a stale id gets a clean result, not a crash (fail-closed).
//
// SCOPE (T3 P2 — honest contract): `shell/delete-entities` deletes ONLY SPACE
// entities (`document.spaces[].entities`). A block-definition / ATTDEF id (e.g. an
// `attdef-*` from CREATE_ATTDEF, which lives in `document.blocks[].entities`) is NOT
// deletable through this path — it is one of the gracefully-skipped no-op ids above.
// The turn's `removedEntityIds` (surfaced by the safe contour's before/after diff)
// EXCLUDES a non-removed id, so a DELETE_ENTITIES of a block-def id is NON-SILENT:
// the agent sees the id missing from `removedEntityIds`. This space-entity scope is
// surfaced to the agent on the manifest (`MANIFEST_PRECONDITION_NOTES.DELETE_ENTITIES`).
export const deleteEntitiesAiSchema = z.object({
  action: z.literal("DELETE_ENTITIES"),
  entityIds: z.array(z.string().min(1)).min(1),
});
export type DeleteEntitiesAiCommand = z.infer<typeof deleteEntitiesAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Discriminated unions + domain routing
// ═══════════════════════════════════════════════════════════════════════════

// Base-primitive CREATE family (LINE / CIRCLE / ARC / PLINE).
export const basePrimAiCommandSchema = z.discriminatedUnion("action", [
  createLineAiSchema,
  createCircleAiSchema,
  createArcAiSchema,
  createPlineAiSchema,
]);
export type BasePrimAiCommand = z.infer<typeof basePrimAiCommandSchema>;

// Base MODIFY family (MOVE / COPY / ROTATE / DELETE_ENTITIES).
export const baseModifyAiCommandSchema = z.discriminatedUnion("action", [
  moveAiSchema,
  copyAiSchema,
  rotateAiSchema,
  deleteEntitiesAiSchema,
]);
export type BaseModifyAiCommand = z.infer<typeof baseModifyAiCommandSchema>;

// Combined domain union (all 8 arms) — the shape the dispatcher translates.
export const basePrimDomainAiCommandSchema = z.discriminatedUnion("action", [
  createLineAiSchema,
  createCircleAiSchema,
  createArcAiSchema,
  createPlineAiSchema,
  moveAiSchema,
  copyAiSchema,
  rotateAiSchema,
  deleteEntitiesAiSchema,
]);
export type BasePrimDomainAiCommand = z.infer<
  typeof basePrimDomainAiCommandSchema
>;

// A batch of base-prim/base-modify commands the AI can emit in one response.
export const basePrimDomainAiCommandBatchSchema = z.array(
  basePrimDomainAiCommandSchema,
);
export type BasePrimDomainAiCommandBatch = z.infer<
  typeof basePrimDomainAiCommandBatchSchema
>;

// Convenience: literal action names for the router, guard map, error messages
// and telemetry. The two arrays are DISJOINT — `BASE_PRIM_AI_ACTIONS` is the
// create family, `BASE_MODIFY_AI_ACTIONS` is the modify family — and together
// they cover every action in the exhaustive guard Record.
export const BASE_PRIM_AI_ACTIONS = [
  "CREATE_LINE",
  "CREATE_CIRCLE",
  "CREATE_ARC",
  "CREATE_PLINE",
] as const;
export type BasePrimAiAction = (typeof BASE_PRIM_AI_ACTIONS)[number];

export const BASE_MODIFY_AI_ACTIONS = [
  "MOVE",
  "COPY",
  "ROTATE",
  "DELETE_ENTITIES",
] as const;
export type BaseModifyAiAction = (typeof BASE_MODIFY_AI_ACTIONS)[number];

const BASE_PRIM_ACTION_SET: ReadonlySet<string> = new Set(BASE_PRIM_AI_ACTIONS);
const BASE_MODIFY_ACTION_SET: ReadonlySet<string> = new Set(
  BASE_MODIFY_AI_ACTIONS,
);

/**
 * Router predicate: does this AI command belong to the base-PRIMITIVE (create)
 * family? The base-prim / base-modify / block / entity / draw-modify action
 * literal sets are all DISJOINT, so a membership test on `action` is a sound
 * discriminator. The type predicate lets `aiDispatcher.ts` narrow the broad
 * `AiCommand` union without importing it here (which would create a cycle).
 */
export function isBasePrimAiCommand(command: {
  action: string;
}): command is BasePrimAiCommand {
  return BASE_PRIM_ACTION_SET.has(command.action);
}

/**
 * Router predicate: does this AI command belong to the base-MODIFY family
 * (MOVE / COPY / ROTATE)? Disjoint from {@link isBasePrimAiCommand} and every
 * other domain predicate.
 */
export function isBaseModifyAiCommand(command: {
  action: string;
}): command is BaseModifyAiCommand {
  return BASE_MODIFY_ACTION_SET.has(command.action);
}
